import { Tabs, Tooltip } from "antd"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { ROLE_IDS } from "../../../../constants/roles.ts"
import { RoutesPaths } from "../../../../constants/routes.ts"
import { useAppSelector } from "../../../../hooks/use-app-selector.ts"
import {
  selectActiveBranchId,
  selectIsMultiBranch,
  selectIsOwner
} from "../../../Branches/redux/branches.slice.ts"
import { type PermissionFeatures } from "../../../SettingsPermissions/types/permissions.types.ts"

type TabItem = {
  key: string
  label: string
  tooltip?: string
  isVisible?: (permissions: PermissionFeatures) => boolean | undefined
  // Where clicking the tab navigates to, when different from `key` (a
  // grouping tab like "Company Defaults" has no page of its own — it
  // opens on its first member).
  navigateTo?: string
  // Extra pathname prefixes that also count as "this tab is active" —
  // again, for a grouping tab whose members live at their own routes.
  matchPrefixes?: string[]
}

// A tab stays active on its nested pages, so the longest matching key wins.
const resolveActiveKey = (items: TabItem[], pathname: string) =>
  items
    .filter(({ key, matchPrefixes }) =>
      [key, ...(matchPrefixes || [])].some(
        (prefix) => pathname === prefix || pathname?.startsWith(`${prefix}/`)
      )
    )
    .reduce((longest, item) => {
      return item.key.length > longest.length ? item.key : longest
    }, "")

interface SettingsLayoutTabsProps {
  permissions: PermissionFeatures
}

export default function SettingsLayoutTabs({
  permissions
}: SettingsLayoutTabsProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const pathname = useLocation()?.pathname
  const roleId = useAppSelector((state) => state.auth.userInfo?.role?.id)
  const automationOn = useAppSelector(
    (state) => !!state.auth.userInfo?.feature_lead_automation
  )
  // A real, Django-provisioned GCC Owner (`invite_gcc_admin`) is seeded
  // with the legacy `role=TEAM_MEMBER` — `GccAssignment.is_owner` (here,
  // `selectIsOwner`) is the actual ownership signal, same as the route
  // guard (`BranchesOwnerGate`) already uses. Without this, such an owner
  // sees every other Settings tab but not Branches itself.
  const isOwner = useAppSelector(selectIsOwner)
  // "Company & System" means a different record depending on the active
  // workspace (see `SettingsCompanyPage`) — the tab label says so, rather
  // than reading the same as it does for Company HQ.
  const isBranchWorkspace = !!useAppSelector(selectActiveBranchId)
  // Only Company HQ actually administers every branch — create, archive,
  // restore (`apps.user.branch_service`, exposed only via Settings ->
  // Branches). A specific branch workspace has no equivalent screen, so
  // the tab itself is hidden there; a single-operation account (never
  // multi-branch, so this is always true for it) keeps its one
  // discoverable Add-branch entry exactly as before.
  const isMultiBranch = useAppSelector(selectIsMultiBranch)
  const branchesTabIsHQOnly = isMultiBranch && isBranchWorkspace

  const canManageConfiguration =
    roleId === ROLE_IDS.ADMIN_GCC || roleId === ROLE_IDS.ADMIN_PLATFORM || isOwner

  // The whole permission tree, not just its `settings` branch: Lead Life Cycle
  // is gated on a leads right, so narrowing the prop would put that tab out of
  // reach of its own permission.
  const allTabs: TabItem[] = [
    {
      key: RoutesPaths.settings.profile,
      label: "Profile & User"
    },
    {
      key: RoutesPaths.settings.company,
      label: isBranchWorkspace ? "Branch & System" : "Company & System",
      isVisible: (p) => p?.settings?.company?.settings_company_view
    },
    // Configurations/Required Information/Templates are grouped under one
    // "Company Defaults" tab — the authoritative, reusable baseline every
    // branch inherits from or overrides — rather than three separate top-
    // level tabs. Each still lives at its own pre-existing route; this tab
    // opens on the first and the group's own CompanyDefaultsSubTabs strip
    // (rendered by those three pages) switches between all of them.
    // Company-wide, like Branches — a branch workspace manages its own
    // settings only, never the shared baseline every branch inherits from.
    {
      key: "company-defaults",
      label: "Company Defaults",
      navigateTo: RoutesPaths.settings.configuration,
      matchPrefixes: [
        RoutesPaths.settings.configuration,
        RoutesPaths.settings.formFieldRequirements,
        RoutesPaths.settings.templates
      ],
      isVisible: (p) =>
        !isBranchWorkspace &&
        (canManageConfiguration ||
          p?.settings?.form_requirements?.form_requirements_manage ||
          p?.settings?.project_templates?.settings_template_view ||
          p?.settings?.document_templates?.settings_proposal_template_view)
    },
    {
      key: RoutesPaths.settings.permissions,
      label: "Permissions",
      isVisible: (p) => p?.settings?.permissions?.settings_permissions_view
    },
    {
      key: RoutesPaths.settings.notifications,
      label: "Notifications"
    },
    {
      key: RoutesPaths.settings.branches,
      label: "Branches",
      isVisible: () => canManageConfiguration && !branchesTabIsHQOnly
    },
    {
      key: RoutesPaths.settings.automation,
      label: t("settingsAutomation.tab"),
      isVisible: (p) =>
        automationOn && p?.settings?.automations?.settings_automation_view
    }
  ]

  const items = allTabs?.filter(
    ({ isVisible }) => !isVisible || !!isVisible(permissions)
  )

  const activeKey = resolveActiveKey(items, pathname)

  return (
    <div className="border-b border-gray-200">
      <Tabs
        activeKey={activeKey || items?.[0]?.key}
        items={items?.map(({ key, label, tooltip }) => ({
          key,
          label: tooltip ? (
            <Tooltip title={tooltip}>
              <span>{label}</span>
            </Tooltip>
          ) : (
            label
          )
        }))}
        onChange={(key) => {
          const target = items?.find((item) => item.key === key)
          navigate(target?.navigateTo || key)
        }}
        className="[&_.ant-tabs-nav]:mb-0 [&_.ant-tabs-tab]:py-1"
      />
    </div>
  )
}
